/**
 * API client for backend communication
 */

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:3001';

export interface ApiError {
  success: false;
  error: {
    code: string;
    message: string;
    details?: any;
  };
}

export class ApiClientError extends Error {
  constructor(
    public statusCode: number,
    public code: string,
    message: string,
    public details?: any
  ) {
    super(message);
    this.name = 'ApiClientError';
  }
}

/**
 * Get auth token from Firebase Auth
 */
async function getAuthToken(): Promise<string | null> {
  // This will be implemented when we set up Firebase Auth
  // For now, return null (no auth)
  return null;
}

/**
 * Make API request
 */
async function request<T>(
  endpoint: string,
  options: RequestInit = {}
): Promise<T> {
  const url = `${API_BASE_URL}${endpoint}`;
  
  const token = await getAuthToken();
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...(options.headers as Record<string, string> || {}),
  };

  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }

  const response = await fetch(url, {
    ...options,
    headers,
  });

  const data = await response.json();

  if (!response.ok) {
    if (data.error) {
      throw new ApiClientError(
        response.status,
        data.error.code || 'UNKNOWN_ERROR',
        data.error.message || 'An error occurred',
        data.error.details
      );
    }
    throw new ApiClientError(
      response.status,
      'HTTP_ERROR',
      `HTTP ${response.status}: ${response.statusText}`
    );
  }

  return data;
}

/**
 * GET request
 */
export async function get<T>(endpoint: string): Promise<T> {
  return request<T>(endpoint, { method: 'GET' });
}

/**
 * POST request
 */
export async function post<T>(endpoint: string, body?: any): Promise<T> {
  return request<T>(endpoint, {
    method: 'POST',
    body: body ? JSON.stringify(body) : undefined,
  });
}

/**
 * PUT request
 */
export async function put<T>(endpoint: string, body?: any): Promise<T> {
  return request<T>(endpoint, {
    method: 'PUT',
    body: body ? JSON.stringify(body) : undefined,
  });
}

/**
 * DELETE request
 */
export async function del<T>(endpoint: string): Promise<T> {
  return request<T>(endpoint, { method: 'DELETE' });
}

// API endpoints
export const api = {
  // Products
  products: {
    getAll: () => get<{ products: any[] }>('/api/products'),
    getBySlug: (slug: string) => get<{ product: any | null }>(`/api/products/${slug}`),
  },

  // Categories
  categories: {
    getAll: () => get<{ categories: any[] }>('/api/categories'),
    getBySlug: (slug: string) => get<{ category: any | null }>(`/api/categories/${slug}`),
  },

  // Contact
  contact: {
    send: (data: { name: string; email: string; subject?: string; message: string }) =>
      post<{ success: boolean; message: string }>('/api/contact', data),
  },

  // Checkout
  checkout: {
    getConfig: () =>
      get<{
        options: {
          inpost: { id: string; name: string; price: number; enabled: boolean };
          courier: { id: string; name: string; price: number; enabled: boolean };
        };
        vatRate: number;
        freeShippingThreshold?: number;
      }>('/api/checkout/config'),

    createSession: (data: {
      amount: number;
      currency: string;
      orderId?: string;
      customerEmail: string;
      customerName: string;
      lineItems: Array<{ name: string; amount: number; quantity: number }>;
      successUrl: string;
      cancelUrl: string;
      items?: any[];
      shipping?: any;
      customer?: any;
    }) =>
      post<{ url: string; orderId: string }>('/api/checkout', data),
    
    calculatePricing: (data: {
      items: Array<{ productId: string; price: number; quantity: number }>;
      shippingMethod?: 'inpost' | 'courier';
    }) =>
      post<{
        subtotal: number;
        shipping: number;
        vat: number;
        total: number;
        netSubtotal: number;
        netShipping: number;
        freeShippingThreshold?: number;
        freeShippingApplied: boolean;
      }>('/api/checkout/pricing', data),
  },

  // Generic methods for direct access
  get,
  post,
  put,
  del,
};

